import React, { useState } from 'react';
import { ActivityIcon, ChevronRightIcon, EyeIcon, HandIcon, LightbulbIcon, PackageCheckIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { easeEnter } from '../utils/motion';
const stages = [{
  id: 'attention',
  label: 'Attention',
  icon: EyeIcon,
  description: 'Reach a high-fit audience at a moment they are ready to discover.',
  signal: 'Qualified reach',
  value: '84.2K',
  detail: 'A location, event, or cultural moment gives your product context.'
}, {
  id: 'participation',
  label: 'Participation',
  icon: HandIcon,
  description: 'Give people a reason to actively choose your brand experience.',
  signal: 'Active actions',
  value: '19.6K',
  detail: 'Play, surprise, and relevance turn passive exposure into a decision.'
}, {
  id: 'trial',
  label: 'Product trial',
  icon: PackageCheckIcon,
  description: 'Connect the physical product to a simple, traceable redemption.',
  signal: 'QR redemptions',
  value: '12.4K',
  detail: 'A sample is no longer anonymous once it becomes a digital handoff.'
}, {
  id: 'signal',
  label: 'Consumer signal',
  icon: ActivityIcon,
  description: 'Capture feedback, intent, sentiment, and the context behind trial.',
  signal: 'Feedback rate',
  value: '38%',
  detail: 'The response tells your brand what happened after the product was tried.'
}, {
  id: 'optimize',
  label: 'Smarter next activation',
  icon: LightbulbIcon,
  description: 'Use evidence to choose where, who, and how to activate next.',
  signal: 'Next action',
  value: 'Ready',
  detail: 'Learning compounds so every activation starts smarter than the last.'
}];
export function HowItWorks() {
  const [activeIndex, setActiveIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const active = stages[activeIndex];
  const Icon = active.icon;
  return <section id="media-loop" className="bg-paper px-5 py-24 md:px-10 md:py-32" aria-labelledby="loop-heading" data-id="element-473" data-mp="3l08u6s7zdvp9">
      <div className="mx-auto max-w-shell" data-id="element-474" data-mp="3pytu0qjbmdd6">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-end" data-id="element-475" data-mp="3u0f4k58dufk7">
          <div data-id="element-476" data-mp="2tufgr1w1hpt">
            <p className="text-xs font-extrabold tracking-[0.16em] text-ink/55" data-id="element-477" data-mp="2tlyoo2n9s9c">
              THE SAMPLER MEDIA LOOP
            </p>
            <h2 id="loop-heading" className="mt-5 font-display text-4xl font-bold leading-[0.9] md:text-6xl" data-id="element-478" data-mp="7s5o4ygg5m6g">
              A product trial should not be the end of the story.
            </h2>
          </div>
          <p className="max-w-xl text-lg leading-relaxed text-ink/70" data-id="element-479" data-mp="lkc4akjlnt5w">
            Sampler makes each consumer experience a media loop: attention
            becomes participation, participation becomes trial, and trial becomes
            the evidence behind a smarter next move.
          </p>
        </div>

        <div className="mt-14 border border-ink/15" data-id="element-480" data-mp="15m6pahdjycmp">
          <div className="grid lg:grid-cols-[0.7fr_1.3fr]" data-id="element-481" data-mp="15o17hnxfvqn3">
            <div className="flex gap-1 overflow-x-auto border-b border-ink/15 p-3 lg:flex-col lg:overflow-visible lg:border-b-0 lg:border-r" role="tablist" aria-label="Media loop stages" data-id="element-482" data-mp="10n3tzondjkrx">
              {stages.map((stage, index) => {
              const StageIcon = stage.icon;
              const isActive = index === activeIndex;
              return <button key={stage.id} type="button" role="tab" aria-selected={isActive} onClick={() => setActiveIndex(index)} className={`flex min-w-[170px] items-center gap-3 px-3 py-3 text-left transition-colors lg:min-w-0 ${isActive ? 'bg-ink text-paper' : 'text-ink/65 hover:bg-ink/5'}`} data-id="element-483" data-mp="1jf62gcpv3ur3" data-mpi={index}>
                    <StageIcon size={17} className={isActive ? 'text-signal' : 'text-ink/45'} aria-hidden="true" data-id="element-484" data-mp="1mtefnxoey3se" />
                    <span className="whitespace-nowrap text-sm font-bold" data-id="element-485" data-mp="1dw4225o4i2kb">
                      {stage.label}
                    </span>
                    <ChevronRightIcon size={15} aria-hidden="true" className={`ml-auto shrink-0 ${isActive ? 'text-paper' : 'text-ink/30'}`} data-id="element-486" data-mp="25kp13zbddocw" />
                  </button>;
            })}
            </div>

            <div className="relative min-h-[380px] bg-paper p-6 md:p-10" role="tabpanel" data-id="element-487" data-mp="20tyu6hnapdg3">
              <AnimatePresence mode="wait" data-id="element-488" data-mp="2oj9rtq9cmrq0">
                <motion.div key={active.id} initial={reduceMotion ? false : {
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={reduceMotion ? undefined : {
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.24,
                ease: easeEnter
              }} data-id="element-489" data-mp="2tarrmazmym9e">
                  <span className="grid h-12 w-12 place-items-center border border-ink/20" data-id="element-490" data-mp="8srq1exnj0gj">
                    <Icon size={22} className="text-signal" aria-hidden="true" data-id="element-491" data-mp="3y4cuc83lsh9" />
                  </span>
                  <p className="mt-8 text-xs font-extrabold tracking-[0.14em] text-ink/55" data-id="element-492" data-mp="1kc48hazwuvyp">
                    0{activeIndex + 1} / 05
                  </p>
                  <h3 className="mt-3 font-display text-4xl font-bold" data-id="element-493" data-mp="1mdldidgdkmft">
                    {active.label}
                  </h3>
                  <p className="mt-4 max-w-md text-lg leading-relaxed text-ink/75" data-id="element-494" data-mp="1rfki1qnuppec">
                    {active.description}
                  </p>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-ink/60" data-id="element-495" data-mp="21c6ym3959uak">
                    {active.detail}
                  </p>

                  <div className="mt-10 inline-flex items-center gap-5 border border-ink/20 px-5 py-4" data-id="element-496" data-mp="13pq75en3eawq">
                    <div data-id="element-497" data-mp="18n9nck3coxrb">
                      <p className="text-xs font-extrabold uppercase tracking-[0.1em] text-ink/50" data-id="element-498" data-mp="1dm302aatckqf">
                        {active.signal}
                      </p>
                      <p className="mt-1 font-display text-3xl font-bold" data-id="element-499" data-mp="1ie48fl4v1rb6">
                        {active.value}
                      </p>
                    </div>
                    <span className="h-9 w-px bg-ink/15" data-id="element-500" data-mp="pvgqgel5useb" />
                    <span className="text-xs font-extrabold tracking-[0.1em] text-signal" data-id="element-501" data-mp="336rxaquqjqdh">
                      LIVE
                    </span>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="absolute bottom-7 right-7 hidden items-center gap-2 md:flex" aria-hidden="true" data-id="element-502" data-mp="2u5k8cze401w2">
                {stages.map((stage, index) => <span key={stage.id} className={`h-1.5 transition-all duration-200 ${index === activeIndex ? 'w-8 bg-signal' : 'w-1.5 bg-ink/25'}`} data-id="element-503" data-mp="2k85yxjqlsdd9" data-mpi={index} />)}
              </div>
            </div>
          </div>
        </div>
        <p className="mt-5 text-xs font-bold text-ink/55" data-id="element-504" data-mp="26ic66qbz98y9">
          Illustrative campaign journey and signals.
        </p>
      </div>
    </section>;
}